/**
 * Page Types
 * TypeScript interfaces for the dynamic pages system.
 * Used by both the builder (editing) and viewer (rendering) apps.
 */

import type { SectionSettings } from '../constants/section-registry'

// =============================================================================
// Page Types
// =============================================================================

export type DynamicPageType =
  | 'home'
  | 'about'
  | 'contact'
  | 'fleet'
  | 'faq'
  | 'custom'

export interface Page {
  id: string
  account_id: string
  slug: string
  title: string
  page_type: DynamicPageType
  meta_title: string | null
  meta_description: string | null
  og_image_url: string | null
  is_published: boolean
  show_in_navigation: boolean
  show_in_footer: boolean
  /** Whether the site header renders on this page (false when a section supplies its own nav). */
  show_header: boolean
  navigation_order: number
  navigation_label: string | null
  created_at: string
  updated_at: string
}

export interface PageInsert {
  account_id: string
  slug: string
  title: string
  page_type?: DynamicPageType
  meta_title?: string | null
  meta_description?: string | null
  og_image_url?: string | null
  is_published?: boolean
  show_in_navigation?: boolean
  show_in_footer?: boolean
  show_header?: boolean
  navigation_order?: number
  navigation_label?: string | null
}

export interface PageUpdate {
  slug?: string
  title?: string
  page_type?: DynamicPageType
  meta_title?: string | null
  meta_description?: string | null
  og_image_url?: string | null
  is_published?: boolean
  show_in_navigation?: boolean
  show_in_footer?: boolean
  show_header?: boolean
  navigation_order?: number
  navigation_label?: string | null
}

// =============================================================================
// Page Section Types
// =============================================================================

export interface PageSection {
  id: string
  account_id: string
  page_id: string
  section_type: string
  display_order: number
  is_visible: boolean
  settings: SectionSettings
  content: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface PageSectionInsert {
  account_id: string
  page_id: string
  section_type: string
  display_order?: number
  is_visible?: boolean
  settings?: SectionSettings
  content?: Record<string, unknown>
}

export interface PageSectionUpdate {
  section_type?: string
  display_order?: number
  is_visible?: boolean
  settings?: SectionSettings
  content?: Record<string, unknown>
}

// =============================================================================
// Combined Types
// =============================================================================

export interface PageWithSections extends Page {
  sections: PageSection[]
}

export interface PageWithSectionCount extends Page {
  section_count: number
}

// =============================================================================
// Navigation Types
// =============================================================================

export interface NavigationItem {
  id: string
  slug: string
  title: string
  label: string // navigation_label or title fallback
  order: number
}

// =============================================================================
// Reserved Slugs
// =============================================================================

/**
 * Slugs that are reserved for system routes and cannot be used for custom pages.
 * Note: System page types (home, about, contact, fleet, faq) use these slugs
 * by default but can coexist since they are page_type specific.
 */
export const RESERVED_SLUGS = [
  'book',
  'booking',
  'api',
  'p', // Legacy custom page prefix
  'section-preview', // Internal section/variation preview tool
] as const

export type ReservedSlug = (typeof RESERVED_SLUGS)[number]

/**
 * Check if a slug is reserved
 */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.includes(slug as ReservedSlug)
}

/**
 * Validate a page slug
 * Returns an error message if invalid, null if valid
 */
export function validatePageSlug(
  slug: string,
  options?: { allowEmpty?: boolean }
): string | null {
  // Allow empty string for home page
  if (slug === '') {
    return options?.allowEmpty ? null : 'Slug cannot be empty'
  }

  // Check for reserved slugs
  if (isReservedSlug(slug)) {
    return `"${slug}" is a reserved slug and cannot be used`
  }

  // Validate format: lowercase, alphanumeric, hyphens
  const slugRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/
  if (!slugRegex.test(slug)) {
    return 'Slug must contain only lowercase letters, numbers, and hyphens'
  }

  // No consecutive hyphens
  if (slug.includes('--')) {
    return 'Slug cannot contain consecutive hyphens'
  }

  return null
}

/**
 * Generate a slug from a title
 */
export function generateSlugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove consecutive hyphens
    .replace(/^-|-$/g, '') // Trim hyphens from start/end
}

/**
 * Generate a unique slug by appending a suffix to avoid conflicts
 * @param baseSlug - The original slug to duplicate
 * @param existingSlugs - Array of existing slugs to check against
 * @returns A unique slug
 *
 * @example
 * generateUniqueSlug('about', ['about', 'contact']) // returns 'about-copy'
 * generateUniqueSlug('about', ['about', 'about-copy']) // returns 'about-1'
 * generateUniqueSlug('about', ['about', 'about-copy', 'about-1']) // returns 'about-2'
 */
export function generateUniqueSlug(
  baseSlug: string,
  existingSlugs: string[]
): string {
  // Handle empty base slug (e.g., home page)
  const slugBase = baseSlug || 'page'

  // Try with "-copy" suffix first
  const copySlug = `${slugBase}-copy`
  if (!existingSlugs.includes(copySlug)) {
    return copySlug
  }

  // Try with numeric suffixes
  let counter = 1
  while (counter <= 100) {
    const numberedSlug = `${slugBase}-${counter}`
    if (!existingSlugs.includes(numberedSlug)) {
      return numberedSlug
    }
    counter++
  }

  // Safety fallback: use timestamp suffix
  return `${slugBase}-${Date.now()}`
}
